<template>
  <SidePopup
    :title="title"
    :loading="loading"
    v-on:close="onClose"
    @submit="onSubmit"
    routeback="/fornecedor/cadastro/tipos-venda"
  >
    <v-alert
      v-if="isRegistroSistema"
      color="primario"
      outlined
      dense
      class="tipos-venda-form-alert mb-4"
      icon="fas fa-lock"
    >
      <strong>{{ original.nome }}</strong> é um registro do sistema: nome e
      código não podem ser alterados e ele não pode ser excluído. Formas de
      pagamento, status, padrão e as opções abaixo continuam editáveis.
    </v-alert>

    <v-row no-gutters>
      <v-col cols="12">
        <FormField
          label="Nome do tipo de venda"
          v-model="form.nome"
          :disabled="isRegistroSistema"
          :validation="$v.form.nome"
        />
      </v-col>
      <v-col cols="12">
        <FormField
          label="Código"
          v-model="form.codigo"
          :maxLength="codigoMaxLength"
          counter
          :disabled="isRegistroSistema"
          :validation="$v.form.codigo"
          :personalizedMessage="mensagemCodigo"
          hint="Enviado ao ERP como condição de venda (condvenda)"
          persistenthint
        />
      </v-col>
      <v-col cols="12" class="mt-2">
        <FormCheckbox
          v-model="form.padrao"
          label="Definir como tipo de venda padrão"
          :disabled="eraPadrao"
          :help="
            eraPadrao
              ? 'Para trocar o padrão, marque outro tipo de venda como padrão.'
              : 'O tipo padrão aparece primeiro no LifeGrowth. O padrão atual deixa de ser padrão ao salvar.'
          "
        />
      </v-col>
      <v-col cols="12">
        <FormSwitch
          v-model="form.ativo"
          label="Tipo de venda ativo"
          :disabled="form.padrao"
          :help="
            form.padrao
              ? 'O tipo de venda padrão precisa permanecer ativo.'
              : 'Inativos somem do LifeGrowth, mas mantêm as formas de pagamento vinculadas.'
          "
        />
      </v-col>
      <v-col cols="12">
        <FormSwitch
          v-model="form.pularAguardandoPagamento"
          label="Pular etapa aguardando pagamento"
          help="Pedidos deste tipo não ficam aguardando pagamento."
        />
      </v-col>
      <v-col cols="12">
        <FormSwitch
          v-model="form.somenteAtendente"
          label="Somente atendente"
          help="Restringe o uso deste tipo de venda a atendentes."
        />
      </v-col>
      <v-col cols="12" class="mt-2">
        <FormAutoComplete
          label="Formas de pagamento vinculadas"
          placeholder="Todas as formas da loja"
          v-model="form.idformapagtos"
          :options="formasPagamentoOptions"
          :loading="loadingFormas"
          multiple
          clearable
          hint="Sem formas selecionadas, todas as formas da loja ficam disponíveis para este tipo."
          persistenthint
        />
      </v-col>
    </v-row>
    <template v-slot:buttons>
      <FormButton text="Salvar" submit type="submissao" :loading="saving" />
    </template>
  </SidePopup>
</template>

<script>
import SidePopup from "@/components/SidePopup";
import FormField from "@/components/form/FormField";
import FormSwitch from "@/components/form/FormSwitch";
import FormCheckbox from "@/components/form/FormCheckbox";
import FormAutoComplete from "@/components/form/FormAutocomplete";
import FormButton from "@/components/form/FormButton";
import { required, maxLength } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";
import { FORMA_PAGAMENTO_LIST } from "@/store/actions/formaPagamento";
import {
  TIPO_VENDA_GET_BY_ID,
  TIPO_VENDA_CREATE,
  TIPO_VENDA_UPDATE
} from "@/store/actions/tipoVenda";

const BASE_ROUTE = "/fornecedor/cadastro/tipos-venda";
const CODIGO_MAX_LENGTH = 20;

const formVazio = () => ({
  nome: "",
  codigo: "",
  ativo: true,
  padrao: false,
  pularAguardandoPagamento: false,
  somenteAtendente: false,
  idformapagtos: []
});

export default {
  name: "TiposVendaForm",
  components: {
    SidePopup,
    FormField,
    FormSwitch,
    FormCheckbox,
    FormAutoComplete,
    FormButton
  },
  validations: {
    form: {
      nome: { required },
      codigo: { required, maxLength: maxLength(CODIGO_MAX_LENGTH) }
    }
  },
  data: () => ({
    mode: "add",
    loading: false,
    loadingFormas: false,
    saving: false,
    codigoMaxLength: String(CODIGO_MAX_LENGTH),
    original: null,
    form: formVazio()
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "getFormasPagamento"]),
    title() {
      return this.mode === "edit"
        ? "Editar tipo de venda"
        : "Novo tipo de venda";
    },
    isRegistroSistema() {
      return !!(this.original && this.original.registro_sistema);
    },
    eraPadrao() {
      return !!(this.original && this.original.padrao);
    },
    mensagemCodigo() {
      return `Informe o código (até ${CODIGO_MAX_LENGTH} caracteres)`;
    },
    formasPagamentoOptions() {
      return (this.getFormasPagamento || []).map(forma => ({
        value: forma.idformapagto,
        text: forma.description || forma.descricao
      }));
    }
  },
  watch: {
    "form.padrao"(padrao) {
      if (padrao) {
        this.form.ativo = true;
      }
    }
  },
  created() {
    this.carregarFormasPagamento();
    const id = this.$route.params.idtipovenda;
    if (id && id !== "novo") {
      this.mode = "edit";
      this.carregarTipoVenda(id);
    }
  },
  methods: {
    onClose() {
      this.$emit("close");
    },
    carregarFormasPagamento() {
      this.loadingFormas = true;
      this.$store
        .dispatch(FORMA_PAGAMENTO_LIST, { idfornecedor: this.getFornecedorId })
        .catch(() => {})
        .finally(() => {
          this.loadingFormas = false;
        });
    },
    carregarTipoVenda(id) {
      this.loading = true;
      this.$store
        .dispatch(TIPO_VENDA_GET_BY_ID, id)
        .then(resp => {
          const tipo = (resp && resp.data) || {};
          this.original = tipo;
          this.form = {
            nome: tipo.nome || "",
            codigo: tipo.codigo || "",
            ativo: tipo.ativo !== false,
            padrao: tipo.padrao === true,
            pularAguardandoPagamento: tipo.pularAguardandoPagamento === true,
            somenteAtendente: tipo.somenteAtendente === true,
            idformapagtos: [...(tipo.idformapagtos || [])]
          };
        })
        .catch(() => {
          this.onClose();
          this.$router.replace(BASE_ROUTE);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    montarPayload() {
      const payload = {
        ativo: this.form.ativo,
        padrao: this.form.padrao,
        pularAguardandoPagamento: this.form.pularAguardandoPagamento,
        somenteAtendente: this.form.somenteAtendente,
        idformapagtos: this.form.idformapagtos || []
      };
      // Venda Normal: nome/código são bloqueados na API, então nem são enviados
      if (!this.isRegistroSistema) {
        payload.nome = (this.form.nome || "").trim();
        payload.codigo = (this.form.codigo || "").trim();
      }
      return payload;
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      const payload = this.montarPayload();
      const request =
        this.mode === "add"
          ? this.$store.dispatch(TIPO_VENDA_CREATE, payload)
          : this.$store.dispatch(TIPO_VENDA_UPDATE, {
              idtipovenda: this.original.idtipovenda,
              payload
            });

      this.saving = true;
      request
        .then(() => {
          this.$vueOnToast.pop(
            "success",
            this.mode === "add"
              ? "Tipo de venda cadastrado com sucesso"
              : "Tipo de venda atualizado com sucesso"
          );
          this.onClose();
          this.$router.replace(BASE_ROUTE);
        })
        .catch(() => {
          /* ERRORS_SHOW na action exibe a mensagem da API */
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.tipos-venda-form-alert {
  font-size: 13px;
  line-height: 1.5;
  border-radius: 14px !important;
}
</style>
